import React, { useReducer, useMemo } from 'react';
import { Box, IconButton, Skeleton, Stack, SxProps, TableCell } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQuery } from '@tanstack/react-query';
import GenericTable from '../Components/GenericTable/GenericTable';
import Api from '../Candidate/API';
import { candidateApplicationsTableColumn } from '../Candidate/config';
import { CandidateReducer, InitialCandidateState } from '../Candidate/reducer';
import { CandidateState, ICandidateApplication } from '../Candidate/types';
import { styles } from '../Templates/styles';
import { styles as candidateApplicationsStyles } from '../Candidate/styles';
import { classes as jobApplicationsStyles } from '../Job/Applications/styles';
import dayjs from 'dayjs';
import { Circle } from '@mui/icons-material';
import { theme } from '@/components/ThemeContext/ThemeObject';

export default function CandidateApplicationsPopover({ candidateId }: { candidateId: number }) {
  const [state, dispatch] = useReducer(CandidateReducer, InitialCandidateState);

  const customizedState = useMemo(() => {
    const filteredColumns = candidateApplicationsTableColumn.map((col) => ({
      ...col,
      sortable: false,
      label: col.id === 'created_at' ? 'Date applied' : col.label,
      enabled: ['job', 'created_at', 'status'].includes(col.id)
    }));

    return {
      ...state,
      candidateApplicationsTableState: {
        ...state.candidateApplicationsTableState,
        columns: filteredColumns
      }
    };
  }, [state]);

  const { data, isLoading } = useQuery({
    queryKey: ['candidateApplicationsPopover', candidateId],
    queryFn: async () => {
      const response = await Api.getCandidateApplications(candidateId, {
        page: 1,
        limit: 5,
        sort: 'created_at',
        sort_order: 'desc'
      });
      return response.res as { applications: ICandidateApplication[] };
    },
    staleTime: 1000 * 60 * 10 // 10 minutes
  });

  const applications: ICandidateApplication[] = (data?.applications || []).slice(0, 5);

  const customHeaderStyle = (id: string): SxProps => {
    switch (id) {
      case 'job':
        return { width: '200px' };
      case 'created_at':
        return { width: '125px' };
      case 'status':
        return { width: '125px' };
      default:
        return { width: '90px' };
    }
  };

  const CandidateApplicationsTableRows = ({
    column,
    item
  }: {
    column: CandidateState['candidateApplicationsTableState']['columns'][number];
    item: ICandidateApplication;
  }) => {
    if (!column.enabled) return null;
    switch (column.id) {
      case 'job':
        return (
          <TableCell key={item.id + column.id} sx={jobApplicationsStyles.noLinkStyling}>
            <a
              href={item.sister_entity_application ? undefined : item.application_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.job_title || '-'}
            </a>
          </TableCell>
        );
      case 'created_at':
        return (
          <TableCell key={item.id + column.id}>
            {dayjs(item.created_at).format('D MMM YYYY') || '-'}
          </TableCell>
        );
      case 'status':
        return (
          <TableCell key={item.id + column.id}>
            {item.status ? (
              <Stack sx={candidateApplicationsStyles.candidateApplicationsTableStatusContainer}>
                <Circle sx={{ fontSize: '0.5rem', color: item.status_colour || '#808080' }} />
                {item.status}
              </Stack>
            ) : (
              '-'
            )}
          </TableCell>
        );
      default:
        return <TableCell></TableCell>;
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        position: 'relative',
        width: '450px',
        maxHeight: '600px',
        backgroundColor: 'white',
        borderRadius: 1,
        boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
        border: '1px solid #e0e0e0'
      }}
    >
      <IconButton
        component="a"
        href={`/admin/candidates/${candidateId}`}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        title="Open candidate in new tab"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1
        }}
      >
        <OpenInNewIcon fontSize="small" sx={{ color: theme.palette.common.darkGrey }} />
      </IconButton>
      {isLoading ? (
        <Skeleton animation="wave" height={60} width="100%" sx={{ marginTop: 1 }} />
      ) : (
        <GenericTable
          items={applications}
          isLoadingItems={false}
          dispatch={dispatch}
          tableState={customizedState.candidateApplicationsTableState}
          paginationState={customizedState.candidateApplicationsTableSettings}
          headerStyle={customHeaderStyle}
          TableRows={CandidateApplicationsTableRows}
          allowMultipleSelection={false}
          label="Candidate Applications"
          emptyMessageOverride={
            applications.length === 0 && (
              <Stack sx={{ ...styles.questionLibraryModalEmptyStateContainer, height: 'auto' }}>
                <Stack sx={styles.questionLibraryModalEmptyStateMessage}>
                  No applications found
                </Stack>
              </Stack>
            )
          }
          customRowStyles={{ '& .MuiTableCell-root': { p: 1 } }}
        />
      )}
    </Box>
  );
}
